'use client';

import { useRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, Dumbbell, QrCode } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import {
  APP_TABS,
  AppTabBar,
  Avatar,
  BrandMark,
  CheckBadge,
  DEMO_BRANDS,
  DEMO_COLORS,
  type DemoProps,
  DemoStage,
  EASE_OUT_EXPO,
  FakeQr,
  PhoneFrame,
  Reveal,
  SPRING_SNAPPY,
  TapRipple,
  localize,
  useDemoTimeline,
} from './demo-kit';

/*
 * Member app, gym: book a HIIT class, book a personal-training session from
 * the member's PT pack, then check in at the gate with the digital card.
 * Everything shown ships today (classes with capacity, PT packages, gate
 * scans); renewal is reminder-based, so the card shows a renewal date only.
 */

const DURATIONS = [1600, 1000, 1700, 1500, 1900, 1400, 2800] as const;

const STEP = {
  tapClass: 1,
  classSheet: 2,
  classBooked: 3,
  ptSheet: 4,
  checkIn: 5,
  checkedIn: 6,
} as const;

const COPY = {
  label: {
    en: 'Animated demo of a gym member app: a member books a HIIT class, books a personal training session from their pack and checks in at the gate with a digital membership card.',
    ar: 'عرض متحرك لتطبيق أعضاء صالة رياضية: عضو يحجز حصة HIIT، ويحجز جلسة تدريب شخصي من باقته، ويسجّل دخوله عند البوابة ببطاقة العضوية الرقمية.',
  },
  greeting: { en: 'Evening, Khalid', ar: 'مساء الخير، خالد' },
  membership: { en: 'Monthly membership', ar: 'عضوية شهرية' },
  active: { en: 'Active · renews 1 Oct', ar: 'فعّالة · تتجدد 1 أكتوبر' },
  checkIn: { en: 'Check in', ar: 'تسجيل دخول' },
  today: { en: "Today's classes", ar: 'حصص اليوم' },
  spots: { en: 'spots', ar: 'أماكن' },
  full: { en: 'Full · Waitlist', ar: 'مكتملة · انتظار' },
  booked: { en: 'Booked', ar: 'محجوزة' },
  pt: { en: 'Personal training', ar: 'تدريب شخصي' },
  ptLeft: { en: 'sessions left', ar: 'جلسات متبقية' },
  book: { en: 'Book', ar: 'احجز' },
  classTitle: { en: 'HIIT', ar: 'HIIT' },
  classWhen: { en: 'Today · 6:30 PM · 45 min', ar: 'اليوم · 6:30 م · 45 دقيقة' },
  coach: { en: 'with Coach Faisal', ar: 'مع المدرب فيصل' },
  spotsLeft: { en: '3 of 12 spots left', ar: 'تبقى 3 من 12 مكانًا' },
  bookClass: { en: 'Book class', ar: 'احجز الحصة' },
  youreIn: { en: "You're in. See you at 6:30 PM", ar: 'تم الحجز. نراك الساعة 6:30 م' },
  calendar: { en: 'Added to your calendar', ar: 'أُضيفت إلى تقويمك' },
  ptTitle: { en: 'Book personal training', ar: 'احجز تدريبًا شخصيًا' },
  usesSession: { en: 'Uses 1 session from your pack', ar: 'تُخصم جلسة واحدة من باقتك' },
  confirm: { en: 'Confirm session', ar: 'تأكيد الجلسة' },
  showAtGate: { en: 'Show at the gate', ar: 'اعرضها عند البوابة' },
  checkedIn: { en: 'Checked in', ar: 'تم تسجيل الدخول' },
  gate: { en: 'Main gate · 6:12 PM', ar: 'البوابة الرئيسية · 6:12 م' },
  nextUp: { en: 'HIIT starts at 6:30 PM', ar: 'حصة HIIT تبدأ 6:30 م' },
} as const;

const CLASSES = [
  { name: 'HIIT', time: { en: '6:30 PM', ar: '6:30 م' }, spots: 3 },
  { name: 'Spin', time: { en: '8:00 PM', ar: '8:00 م' }, spots: 0 },
  { name: 'Mobility', time: { en: '9:00 PM', ar: '9:00 م' }, spots: 8 },
] as const;

const PT_DAYS = [
  { en: 'Sun', ar: 'أحد' },
  { en: 'Mon', ar: 'اثنين' },
  { en: 'Tue', ar: 'ثلاثاء' },
] as const;

const PT_TIMES = [
  { en: '6:00 PM', ar: '6:00 م' },
  { en: '7:00 PM', ar: '7:00 م' },
  { en: '8:00 PM', ar: '8:00 م' },
] as const;

export function GymAppDemo({ locale, className, onLoop }: DemoProps) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, DURATIONS, { staticStep: 0, onLoop });
  const copy = localize(COPY, locale);
  const brand = DEMO_BRANDS.gym;
  const step = timeline.step;

  const classBooked = step >= STEP.classBooked;
  const ptBooked = step >= STEP.checkIn;
  const sheet =
    step === STEP.classSheet || step === STEP.classBooked
      ? 'class'
      : step === STEP.ptSheet
        ? 'pt'
        : null;
  const checkInOpen = step >= STEP.checkIn;

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={copy.label} className={className}>
      <PhoneFrame>
        <div className="absolute inset-0 flex flex-col bg-[#fbfaf8] pt-[44px]">
          <div className="flex items-center justify-between px-5 pb-2 pt-2">
            <div className="flex items-center gap-2.5">
              <BrandMark brand={brand} size={28} />
              <span className="text-[13px] font-semibold text-[#202020]">{brand.name[locale]}</span>
            </div>
            <Avatar initials="KA" tone={2} size={28} />
          </div>

          <p className="px-5 text-[19px] font-bold text-[#202020]">{copy.greeting}</p>

          <div
            className="mx-4 mt-3 flex items-center justify-between rounded-[18px] p-3.5 text-white"
            style={{ background: brand.accent }}
          >
            <div>
              <p className="font-roboto-mono text-[8.5px] uppercase tracking-[0.14em] text-white/75">
                {copy.membership}
              </p>
              <p className="mt-1 text-[12.5px] font-semibold">{copy.active}</p>
            </div>
            <span className="flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1.5 text-[10.5px] font-semibold" style={{ color: brand.accent }}>
              <QrCode className="h-3.5 w-3.5" />
              {copy.checkIn}
            </span>
          </div>

          <p className="mt-4 px-5 font-roboto-mono text-[9.5px] uppercase tracking-[0.12em] text-[#8a857c]">
            {copy.today}
          </p>
          <div className="mt-2 space-y-2 px-4">
            {CLASSES.map((item, index) => {
              const isTarget = index === 0;
              const selected = isTarget && step >= STEP.tapClass && step <= STEP.classBooked;

              return (
                <motion.div
                  key={item.name}
                  className="relative flex h-[46px] items-center justify-between overflow-hidden rounded-[14px] border bg-white px-3.5"
                  initial={false}
                  animate={{
                    borderColor: selected ? brand.accent : DEMO_COLORS.line,
                    backgroundColor: selected ? brand.accentSoft : '#ffffff',
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-[#202020]">{item.name}</span>
                    <span className="text-[11px] text-[#8a857c]">{item.time[locale]}</span>
                  </div>
                  <span
                    className={cn(
                      'flex items-center gap-1 whitespace-nowrap text-[10.5px]',
                      isTarget && classBooked ? 'font-semibold' : 'text-[#8a857c]',
                    )}
                    style={isTarget && classBooked ? { color: DEMO_COLORS.success } : undefined}
                  >
                    {isTarget && classBooked
                      ? copy.booked
                      : item.spots === 0
                        ? copy.full
                        : `${item.spots} ${copy.spots}`}
                    <ChevronRight className="h-3.5 w-3.5 text-[#b5b0a6] rtl:rotate-180" />
                  </span>
                  <TapRipple
                    key={`ripple-${timeline.cycle}`}
                    show={isTarget && step === STEP.tapClass}
                    className="left-1/2 top-1/2"
                  />
                </motion.div>
              );
            })}
          </div>

          <div className="mx-4 mt-3 flex items-center justify-between rounded-[14px] border border-[#ebe7e1] bg-white p-3">
            <div className="flex items-center gap-2.5">
              <span className="flex h-8 w-8 items-center justify-center rounded-[10px]" style={{ background: brand.accentSoft, color: brand.accent }}>
                <Dumbbell className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[12px] font-semibold text-[#202020]">{copy.pt}</p>
                <p className="text-[10.5px] text-[#8a857c]">
                  <AnimatePresence mode="popLayout" initial={false}>
                    <motion.span
                      key={ptBooked ? 'after' : 'before'}
                      className="inline-block"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                    >
                      {ptBooked ? 5 : 6}
                    </motion.span>
                  </AnimatePresence>{' '}
                  {copy.ptLeft}
                </p>
              </div>
            </div>
            <span className="rounded-full px-3 py-1 text-[10.5px] font-semibold text-white" style={{ background: brand.accent }}>
              {copy.book}
            </span>
          </div>

          <AppTabBar
            active={0}
            accent={brand.accent}
            labels={[APP_TABS.home[locale], APP_TABS.book[locale], APP_TABS.bookings[locale], APP_TABS.profile[locale]]}
          />
        </div>

        {/* Sheets */}
        <motion.div
          className="absolute inset-0 z-10 bg-black/25"
          initial={false}
          animate={{ opacity: sheet ? 1 : 0 }}
          style={{ pointerEvents: 'none' }}
          transition={{ duration: 0.3 }}
        />
        <motion.div
          className="absolute inset-x-0 bottom-0 z-20 min-h-[290px] rounded-t-[26px] bg-white px-5 pb-8 pt-3"
          initial={false}
          animate={{ y: sheet ? 0 : '105%' }}
          transition={{ duration: 0.55, ease: EASE_OUT_EXPO }}
        >
          <div className="mx-auto mb-4 h-1 w-9 rounded-full bg-[#dcd7cf]" />
          <AnimatePresence mode="wait" initial={false}>
            {sheet === 'pt' ? (
              <motion.div
                key="pt"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-[16px] font-bold text-[#202020]">{copy.ptTitle}</p>
                <div className="mt-2 flex items-center gap-2">
                  <Avatar initials="FS" tone={3} size={24} />
                  <span className="text-[11.5px] text-[#8a857c]">{copy.coach}</span>
                </div>
                <div className="mt-4 flex gap-2">
                  {PT_DAYS.map((day, index) => (
                    <Chip key={day.en} label={day[locale]} active={index === 1} accent={brand.accent} />
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  {PT_TIMES.map((time, index) => (
                    <Chip key={time.en} label={time[locale]} active={index === 1} accent={brand.accent} delay={0.5} />
                  ))}
                </div>
                <p className="mt-4 text-[11px] text-[#8a857c]">{copy.usesSession}</p>
                <div className="mt-3 flex h-11 items-center justify-center rounded-[14px] text-[13px] font-semibold text-white" style={{ background: brand.accent }}>
                  {copy.confirm}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="class"
                initial={{ opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -16 }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-[18px] font-bold text-[#202020]">{copy.classTitle}</p>
                <p className="mt-0.5 text-[12px] text-[#8a857c]">{copy.classWhen}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Avatar initials="FS" tone={3} size={24} />
                  <span className="text-[11.5px] text-[#8a857c]">{copy.coach}</span>
                </div>
                <div className="mt-4 flex items-center gap-1">
                  {Array.from({ length: 12 }, (_, index) => (
                    <span
                      key={index}
                      className="h-1.5 flex-1 rounded-full"
                      style={{ background: index < (classBooked ? 10 : 9) ? brand.accent : DEMO_COLORS.smoke }}
                    />
                  ))}
                </div>
                <p className="mt-1.5 text-[10.5px] text-[#8a857c]">{copy.spotsLeft}</p>
                <div className="relative mt-5 h-11">
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center rounded-[14px] text-[13px] font-semibold text-white"
                    style={{ background: brand.accent }}
                    initial={false}
                    animate={{ opacity: classBooked ? 0 : 1 }}
                  >
                    {copy.bookClass}
                  </motion.div>
                  <Reveal show={classBooked} className="absolute inset-0 flex items-center gap-2.5 rounded-[14px] bg-[#ddf5ec] px-3">
                    <CheckBadge show={classBooked} size={22} delay={0.1} />
                    <span>
                      <span className="block text-[12px] font-semibold text-[#202020]">{copy.youreIn}</span>
                      <span className="block text-[10px] text-[#2f7a63]">{copy.calendar}</span>
                    </span>
                  </Reveal>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Gate check-in */}
        <motion.div
          className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-[#1c1917] px-6 pt-[44px]"
          initial={false}
          animate={{ opacity: checkInOpen ? 1 : 0 }}
          style={{ pointerEvents: 'none' }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-2 text-white">
            <BrandMark brand={brand} size={24} />
            <span className="text-[13px] font-semibold">{brand.name[locale]}</span>
          </div>
          <div className="relative mt-6 overflow-hidden rounded-[22px] bg-white p-4">
            <FakeQr size={150} seed={31} />
            {step === STEP.checkIn && timeline.isRunning && (
              <motion.span
                className="absolute inset-x-3 h-[2px] rounded-full"
                style={{ background: brand.accent, boxShadow: `0 0 12px ${brand.accent}` }}
                initial={{ top: 16 }}
                animate={{ top: [16, 164, 16] }}
                transition={{ duration: 1.4, ease: 'easeInOut', repeat: Infinity }}
              />
            )}
            <motion.div
              className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-white"
              initial={false}
              animate={{ opacity: step === STEP.checkedIn ? 1 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <CheckBadge show={step === STEP.checkedIn} size={56} delay={0.1} />
              <span className="text-[16px] font-bold text-[#202020]">{copy.checkedIn}</span>
              <span className="text-[11px] text-[#8a857c]">{copy.gate}</span>
            </motion.div>
          </div>
          <p className="mt-4 text-[12px] text-white/70">{copy.showAtGate}</p>
          <Reveal show={step === STEP.checkedIn} delay={0.4} className="mt-6 rounded-full bg-white/10 px-4 py-2 text-[11.5px] text-white">
            {copy.nextUp}
          </Reveal>
        </motion.div>
      </PhoneFrame>
    </DemoStage>
  );
}

function Chip({
  label,
  active,
  accent,
  delay = 0,
}: {
  label: string;
  active: boolean;
  accent: string;
  delay?: number;
}) {
  return (
    <motion.span
      className="flex h-8 flex-1 items-center justify-center rounded-[10px] border text-[11px] font-medium"
      initial={{ backgroundColor: '#ffffff', color: '#202020', borderColor: DEMO_COLORS.line }}
      animate={
        active
          ? { backgroundColor: accent, color: '#ffffff', borderColor: accent }
          : { backgroundColor: '#ffffff', color: '#202020', borderColor: DEMO_COLORS.line }
      }
      transition={{ ...SPRING_SNAPPY, delay: active ? delay + 0.25 : 0 }}
    >
      {label}
    </motion.span>
  );
}
